import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  iconArrowDownStepCourses,
  iconBookStepCourses,
  iconLightBulbStepCourses,
  iconTranslateStepCourses,
  iconUploadStepCourses,
} from '../../assets';
import { useTheme } from '../../theme';

export interface MultilingualText {
  en: string;
  es: string;
}

export interface BasicInfoFormData {
  title: MultilingualText;
  description: MultilingualText;
  categoryId: string;
  tags: string;
  coverFile: File | null;
  /** URL da capa existente (modo edição); exibida quando coverFile é null */
  coverImageUrl?: string;
  durationHours: string;
  effectiveDate: string;
}

const defaultFormData: BasicInfoFormData = {
  title: { en: '', es: '' },
  description: { en: '', es: '' },
  categoryId: '',
  tags: '',
  coverFile: null,
  durationHours: '',
  effectiveDate: '',
};

type LangKey = 'en' | 'es';

/** Chaves de erro por campo (para i18n: courses.create.<key>) */
export type StepOneValidationErrors = Partial<
  Record<'title' | 'description' | 'categoryId' | 'durationHours', string>
>;

/** Valida os campos obrigatórios do Step 1 (*). Retorna valid e erros por campo (chave i18n). */
export function validateStepOne(
  data: BasicInfoFormData | null
): { valid: boolean; errors: StepOneValidationErrors } {
  const errors: StepOneValidationErrors = {};
  if (!data) return { valid: false, errors: { title: 'validation_title_required' } };

  const hasTitle =
    (data.title?.en?.trim() ?? '') !== '' || (data.title?.es?.trim() ?? '') !== '';
  if (!hasTitle) errors.title = 'validation_title_required';

  const hasDescription =
    (data.description?.en?.trim() ?? '') !== '' ||
    (data.description?.es?.trim() ?? '') !== '';
  if (!hasDescription) errors.description = 'validation_description_required';

  if (!(data.categoryId?.trim() ?? '')) errors.categoryId = 'validation_category_required';

  const durationStr = data.durationHours?.trim() ?? '';
  const duration = durationStr ? parseFloat(durationStr) : NaN;
  if (!durationStr || isNaN(duration) || duration <= 0) {
    errors.durationHours = 'validation_duration_required';
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

export interface CreateCourseStepOneProps {
  categoryOptions: { value: string; label: string }[];
  initialData?: Partial<BasicInfoFormData>;
  onChange?: (data: BasicInfoFormData) => void;
  /** Erros de validação por campo (chaves i18n em courses.create.*) */
  validationErrors?: StepOneValidationErrors;
  /** Identificador de teste para o container do Step 1 */
  dataE2eStep?: string;
}

export function CreateCourseStepOne({
  categoryOptions,
  initialData,
  onChange,
  validationErrors,
  dataE2eStep,
}: CreateCourseStepOneProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const coverUploadInputRef = useRef<HTMLInputElement | null>(null);
  const [data, setData] = useState<BasicInfoFormData>({
    ...defaultFormData,
    ...initialData,
  });
  const hasHydratedFromInitial = useRef(false);
  useEffect(() => {
    if (!initialData || hasHydratedFromInitial.current) return;
    const hasLoadedData = Boolean(
      initialData.categoryId ||
      initialData.title?.en ||
      initialData.title?.es
    );
    if (hasLoadedData) {
      hasHydratedFromInitial.current = true;
      setData(prev => ({ ...defaultFormData, ...prev, ...initialData }));
    }
  }, [initialData]);
  const [dragOver, setDragOver] = useState(false);
  const [titleLang, setTitleLang] = useState<LangKey>('en');
  const [descriptionLang, setDescriptionLang] = useState<LangKey>('en');
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);

  const update = useCallback(
    (next: Partial<BasicInfoFormData>) => {
      const nextData = { ...data, ...next };
      setData(nextData);
      onChange?.(nextData);
    },
    [data, onChange]
  );

  // Preview da Cover Image quando o usuário seleciona um arquivo novo.
  // Usamos Object URL para renderizar a imagem localmente antes de enviar ao backend.
  useEffect(() => {
    if (!data.coverFile) {
      setCoverPreviewUrl(null);
      return;
    }
    if (!data.coverFile.type.startsWith('image/')) {
      setCoverPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(data.coverFile);
    setCoverPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [data.coverFile]);

  const maskedIconPrimaryGreen = (
    src: string,
    sizePx: number
  ): React.CSSProperties => ({
    width: sizePx,
    height: sizePx,
    backgroundColor: theme.palette.text.primary,
    WebkitMaskImage: `url(${src})`,
    WebkitMaskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskImage: `url(${src})`,
    maskSize: 'contain',
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    display: 'block',
  });

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.md,
    padding: theme.spacing.xl,
    marginBottom: theme.spacing.xl,
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: theme.spacing.xl,
  };

  const headerRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.xs,
  };

  const iconBoxStyle: React.CSSProperties = {
    width: 40,
    height: 40,
    borderRadius: theme.shape.borderRadius.sm,
    backgroundColor: theme.palette.createCourse.basicInfo.iconBoxBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize.xl,
    color: theme.palette.text.primary,
    margin: 0,
  };

  const sectionSubtitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing.xs,
    marginBottom: theme.spacing.xl,
    paddingLeft: theme.palette.createCourse.basicInfo.sectionSubtitlePaddingLeft,
  };

  const labelRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    marginBottom: theme.spacing.sm,
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.primary,
  };

  const requiredAsteriskStyle: React.CSSProperties = {
    color: theme.palette.error.main,
  };

  const translateIconStyle: React.CSSProperties = {
    width: 18,
    height: 18,
    objectFit: 'contain',
  };

  const segmentedControlWrapStyle: React.CSSProperties = {
    display: 'flex',
    width: '100%',
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius.lg,
    marginBottom: theme.spacing.sm,
    padding: theme.spacing.sm,
    overflow: 'hidden',
    gap: 0,
  };

  const segmentStyle = (
    active: boolean,
    position: 'first' | 'last'
  ): React.CSSProperties => ({
    flex: 1,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    fontWeight: theme.typography.fontWeight.semibold,
    color: active ? theme.palette.text.primary : theme.palette.text.secondary,
    backgroundColor: active
      ? theme.palette.background.paper
      : theme.palette.background.default,
    border: active
      ? `1px solid ${theme.palette.primary.main}`
      : '1px solid transparent',
    borderRadius: active
      ? theme.shape.borderRadius.lg
      : position === 'first'
        ? `${theme.shape.borderRadius.lg} 0 0 ${theme.shape.borderRadius.lg}`
        : `0 ${theme.shape.borderRadius.lg} ${theme.shape.borderRadius.lg} 0`,
    cursor: 'pointer',
    textAlign: 'center',
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: `${theme.spacing.inputY} ${theme.spacing.inputX}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.input.background,
    border: 'none',
    borderRadius: theme.shape.borderRadius.sm,
    boxSizing: 'border-box',
    outline: 'none',
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    minHeight: 100,
    resize: 'vertical',
  };

  const hintRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    marginTop: theme.spacing.sm,
  };

  const hintStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    color: theme.palette.text.secondary,
  };

  const errorStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    color: theme.palette.error.main,
    marginTop: theme.spacing.xs,
    marginBottom: 0,
  };

  const lightBulbIconStyle: React.CSSProperties = {
    width: 18,
    height: 18,
    objectFit: 'contain',
    flexShrink: 0,
  };

  const uploadZoneStyle: React.CSSProperties = {
    border: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius.sm,
    padding: theme.spacing.xl,
    textAlign: 'center',
    backgroundColor: dragOver
      ? theme.palette.createCourse.basicInfo.uploadZoneDragBg
      : theme.palette.background.default,
    cursor: 'pointer',
    transition: 'border-color 0.15s, background-color 0.15s',
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) update({ coverFile: file });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) update({ coverFile: file });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  return (
    <div style={cardStyle} data-e2e={dataE2eStep}>
      <div style={headerRowStyle}>
        <div style={iconBoxStyle}>
          <div
            style={maskedIconPrimaryGreen(iconBookStepCourses, 24)}
            aria-hidden
          />
        </div>
        <div>
          <h2 style={sectionTitleStyle}>
            {t('courses.create.course_information')}
          </h2>
          <p
            style={{
              ...sectionSubtitleStyle,
              paddingLeft: 0,
              marginTop: theme.spacing.xs,
              marginBottom: theme.spacing.xl,
            }}
          >
            {t('courses.create.course_information_hint')}
          </p>
        </div>
      </div>

      {/* Course Title */}
      <div style={sectionStyle}>
        <div style={labelRowStyle}>
          <span style={labelStyle}>{t('courses.create.course_title')}</span>
          <span style={requiredAsteriskStyle}>*</span>
          <img
            src={iconTranslateStepCourses}
            alt=""
            style={translateIconStyle}
            aria-hidden
          />
        </div>
        <div style={segmentedControlWrapStyle}>
          <button
            type="button"
            style={segmentStyle(titleLang === 'en', 'first')}
            onClick={() => setTitleLang('en')}
            aria-pressed={titleLang === 'en'}
          >
            EN
          </button>
          <button
            type="button"
            style={segmentStyle(titleLang === 'es', 'last')}
            onClick={() => setTitleLang('es')}
            aria-pressed={titleLang === 'es'}
          >
            ES
          </button>
        </div>
        <input
          type="text"
          value={data.title[titleLang]}
          onChange={e =>
            update({ title: { ...data.title, [titleLang]: e.target.value } })
          }
          placeholder={t('courses.create.course_title_placeholder')}
          style={{
            ...inputStyle,
            ...(validationErrors?.title
              ? { border: `1px solid ${theme.palette.error.main}` }
              : {}),
          }}
          aria-required
          aria-invalid={Boolean(validationErrors?.title)}
        />
        {validationErrors?.title && (
          <p style={errorStyle} role="alert">
            {t(`courses.create.${validationErrors.title}`)}
          </p>
        )}
        <div style={hintRowStyle}>
          <img
            src={iconLightBulbStepCourses}
            alt=""
            style={lightBulbIconStyle}
            aria-hidden
          />
          <span style={hintStyle}>{t('courses.create.multilingual_hint')}</span>
        </div>
      </div>

      {/* Description */}
      <div style={sectionStyle}>
        <div style={labelRowStyle}>
          <span style={labelStyle}>{t('courses.create.description')}</span>
          <span style={requiredAsteriskStyle}>*</span>
          <img
            src={iconTranslateStepCourses}
            alt=""
            style={translateIconStyle}
            aria-hidden
          />
        </div>
        <div style={segmentedControlWrapStyle}>
          <button
            type="button"
            style={segmentStyle(descriptionLang === 'en', 'first')}
            onClick={() => setDescriptionLang('en')}
            aria-pressed={descriptionLang === 'en'}
          >
            EN
          </button>
          <button
            type="button"
            style={segmentStyle(descriptionLang === 'es', 'last')}
            onClick={() => setDescriptionLang('es')}
            aria-pressed={descriptionLang === 'es'}
          >
            ES
          </button>
        </div>
        <textarea
          value={data.description[descriptionLang]}
          onChange={e =>
            update({
              description: {
                ...data.description,
                [descriptionLang]: e.target.value,
              },
            })
          }
          placeholder={t('courses.create.description_placeholder')}
          style={{
            ...textareaStyle,
            ...(validationErrors?.description
              ? { border: `1px solid ${theme.palette.error.main}` }
              : {}),
          }}
          aria-required
          aria-invalid={Boolean(validationErrors?.description)}
        />
        {validationErrors?.description && (
          <p style={errorStyle} role="alert">
            {t(`courses.create.${validationErrors.description}`)}
          </p>
        )}
        <div style={hintRowStyle}>
          <img
            src={iconLightBulbStepCourses}
            alt=""
            style={lightBulbIconStyle}
            aria-hidden
          />
          <span style={hintStyle}>{t('courses.create.multilingual_hint')}</span>
        </div>
      </div>

      {/* Category e Tags na mesma linha, alinhados na vertical */}
      <div
        style={{
          ...sectionStyle,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: 'auto auto',
          gap: `0 ${theme.spacing.md}`,
          rowGap: theme.spacing.sm,
          alignItems: 'start',
        }}
      >
        <div style={labelRowStyle}>
          <span style={labelStyle}>{t('courses.create.category')}</span>
          <span style={requiredAsteriskStyle}>*</span>
        </div>
        <div style={labelRowStyle}>
          <span style={labelStyle}>{t('courses.create.tags')}</span>
        </div>
        <div
          style={{
            minWidth: 0,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>
            <select
              value={data.categoryId}
              onChange={e => update({ categoryId: e.target.value })}
              style={{
                ...inputStyle,
                minHeight: 40,
                paddingRight: 36,
                appearance: 'none',
                WebkitAppearance: 'none',
                ...(validationErrors?.categoryId
                  ? { border: `1px solid ${theme.palette.error.main}` }
                : {}),
              }}
              aria-required
              aria-invalid={Boolean(validationErrors?.categoryId)}
            >
              <option value="">{t('courses.create.select_category')}</option>
              {categoryOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {validationErrors?.categoryId && (
              <p style={errorStyle} role="alert">
                {t(`courses.create.${validationErrors.categoryId}`)}
              </p>
            )}
          </div>
          <img
            src={iconArrowDownStepCourses}
            alt=""
            width={20}
            height={20}
            style={{
              position: 'absolute',
              right: theme.spacing.sm,
              pointerEvents: 'none',
              objectFit: 'contain',
            }}
            aria-hidden
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <input
            type="text"
            value={data.tags}
            onChange={e => update({ tags: e.target.value })}
            placeholder={t('courses.create.tags_placeholder')}
            style={inputStyle}
          />
          <div style={{ ...hintRowStyle, marginTop: theme.spacing.xs }}>
            <span style={hintStyle}>{t('courses.create.tags_hint')}</span>
          </div>
        </div>
      </div>

      {/* Cover Image */}
      <div style={sectionStyle}>
        <div style={labelRowStyle}>
          <span style={labelStyle}>{t('courses.create.cover_image')}</span>
        </div>
        <div
          style={uploadZoneStyle}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() =>
            document.getElementById('create-course-cover-upload')?.click()
          }
        >
          <input
            id="create-course-cover-upload"
                ref={coverUploadInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          {!data.coverFile && (
            <>
              <img
                src={iconUploadStepCourses}
                alt=""
                width={32}
                height={32}
                style={{ objectFit: 'contain' }}
                aria-hidden
              />
              <p
                style={{
                  margin: theme.spacing.sm,
                  color: theme.palette.text.secondary,
                  fontSize:
                    theme.typography.fontSize.sm,
                }}
              >
                {t('courses.create.upload_cover')}
              </p>
              <p style={{ ...hintStyle, margin: 0 }}>
                {t('courses.create.upload_cover_formats')} {t('courses.create.upload_cover_specs')}
              </p>
            </>
          )}
          {data.coverFile && (
            <div
              style={{
                marginTop: theme.spacing.sm,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: theme.spacing.xs,
              }}
            >
              {coverPreviewUrl ? (
                <img
                  src={coverPreviewUrl}
                  alt=""
                  style={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    objectFit: 'contain',
                    borderRadius: theme.shape.borderRadius.sm,
                    display: 'block',
                  }}
                />
              ) : null}
              <p
                style={{
                  fontSize: theme.typography.fontSize.xs,
                  color: theme.palette.primary.main,
                  wordBreak: 'break-word',
                  margin: 0,
                }}
              >
                {data.coverFile.name}
              </p>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  // Importante: reseta o input file, para permitir selecionar novamente
                  // o mesmo arquivo (o onChange só dispara quando o value muda).
                  if (coverUploadInputRef.current) {
                    coverUploadInputRef.current.value = '';
                  }
                  update({ coverFile: null });
                }}
                style={{
                  marginTop: theme.spacing.xs,
                  padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                  borderRadius: theme.shape.borderRadius.sm,
                  border: `1px solid ${theme.palette.primary.main}`,
                  background: 'transparent',
                  color: theme.palette.primary.main,
                  fontFamily: theme.typography.fontFamily,
                  fontSize: theme.typography.fontSize.xs,
                  fontWeight: theme.typography.fontWeight.semibold,
                  cursor: 'pointer',
                }}
              >
                {t('courses.create.remove_cover') ?? 'Remove image'}
              </button>
            </div>
          )}
          {!data.coverFile && data.coverImageUrl && (
            <div style={{ marginTop: theme.spacing.sm }}>
              <p style={{ ...hintStyle, marginBottom: theme.spacing.xs }}>
                {t('courses.create.current_cover') ?? 'Capa atual:'}
              </p>
              <img
                src={data.coverImageUrl}
                alt=""
                style={{
                  maxWidth: '100%',
                  maxHeight: 200,
                  objectFit: 'contain',
                  borderRadius: theme.shape.borderRadius.sm,
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Duration (Hours) e Effective Date - mesma linha */}
      <div
        style={{
          ...sectionStyle,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: 'auto auto',
          gap: `0 ${theme.spacing.md}`,
          rowGap: theme.spacing.sm,
          alignItems: 'start',
        }}
      >
        <div style={labelRowStyle}>
          <span style={labelStyle}>{t('courses.create.duration_hours')}</span>
          <span style={requiredAsteriskStyle}>*</span>
        </div>
        <div style={labelRowStyle}>
          <span style={labelStyle}>{t('courses.create.effective_date')}</span>
        </div>
        <div style={{ minWidth: 0 }}>
          <input
            type="text"
            inputMode="decimal"
            pattern="[0-9]*[.]?[0-9]*"
            value={data.durationHours}
            onChange={e => {
              // Mantém apenas números e o separador decimal ('.' ou ','),
              // para garantir que "Duration (Hours)" aceite somente valores numéricos.
              const normalized = e.target.value.replace(',', '.');
              const cleaned = normalized.replace(/[^\d.]/g, '');
              const dotIndex = cleaned.indexOf('.');
              const numericOnly =
                dotIndex === -1
                  ? cleaned
                  : cleaned.slice(0, dotIndex + 1) + cleaned.slice(dotIndex + 1).replace(/\./g, '');
              update({ durationHours: numericOnly });
            }}
            placeholder={t('courses.create.duration_hours_placeholder')}
            style={{
              ...inputStyle,
              minHeight: 40,
              ...(validationErrors?.durationHours
                ? { border: `1px solid ${theme.palette.error.main}` }
                : {}),
            }}
            aria-required
            aria-invalid={Boolean(validationErrors?.durationHours)}
          />
          {validationErrors?.durationHours && (
            <p style={errorStyle} role="alert">
              {t(`courses.create.${validationErrors.durationHours}`)}
            </p>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <input
            type="date"
            value={data.effectiveDate}
            onChange={e => update({ effectiveDate: e.target.value })}
            placeholder={t('courses.create.effective_date_placeholder')}
            style={{ ...inputStyle, minHeight: 40 }}
          />
        </div>
      </div>
    </div>
  );
}
